import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  LinearProgress,
  Divider,
  Button,
  Alert
} from '@mui/material';
import {
  MonetizationOn as MoneyIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/numberUtils';
import { MaintenanceRecord } from '../../utils/types';
import { calculateCostSummary } from '../../services/maintenanceCostService';

interface CostSummaryCardProps {
  maintenance: MaintenanceRecord;
  onSetInitialCost?: () => void;
  onUpdateEstimatedCost?: () => void;
  onConfirmApproval?: () => void;
  onDeliveryPayment?: () => void;
  onPostDeliveryPayment?: () => void;
  onAdvancePayment?: () => void;
  onViewPaymentHistory?: () => void;
  onRefund?: () => void;
  showActions?: boolean;
}

const CostSummaryCard: React.FC<CostSummaryCardProps> = ({
  maintenance,
  onSetInitialCost,
  onUpdateEstimatedCost,
  onConfirmApproval,
  onDeliveryPayment,
  onPostDeliveryPayment,
  onAdvancePayment,
  onViewPaymentHistory,
  onRefund,
  showActions = true
}) => {
  const summary = calculateCostSummary(maintenance);
  
  // حساب نسبة الدفع
  const paymentProgress = summary.finalCost > 0 ? 
    (summary.totalPaid / summary.finalCost) * 100 : 0;

  // تحديد المرحلة الحالية
  const getCurrentStage = () => {
    if (!maintenance.initialCost) return 'initial';
    if (maintenance.requireCallBeforeMaintenance && !maintenance.customerApproved) return 'approval';
    if (maintenance.status === 'completed' && !maintenance.finalCost) return 'final';
    if (maintenance.status === 'delivered') return 'completed';
    return 'estimated';
  };

  const currentStage = getCurrentStage();

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'initial': return 'primary';
      case 'estimated': return 'info';
      case 'approval': return 'warning';
      case 'final': return 'success';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStageLabel = (stage: string) => {
    switch (stage) {
      case 'initial': return 'تحديد التكلفة المبدئية';
      case 'estimated': return 'تحديث التكلفة المقدرة';
      case 'approval': return 'انتظار موافقة العميل';
      case 'final': return 'التسليم والتسديد النهائي (نقدي أو دين)';
      case 'completed': return 'تم التسليم';
      default: return 'غير محدد';
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <MoneyIcon color="primary" />
            ملخص التكلفة والتسديد
          </Typography>
          <Chip
            label={getStageLabel(currentStage)}
            color={getStageColor(currentStage) as any}
            size="small"
          />
        </Box>

        {/* المرحلة الحالية */}
        {currentStage === 'approval' && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            icon={<PhoneIcon />}
            action={
              showActions && onConfirmApproval && (
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={onConfirmApproval}
                  startIcon={<PhoneIcon />}
                >
                  تسجيل نتيجة المكالمة
                </Button>
              )
            }
          >
            <Typography variant="body2">
              <strong>انتظار موافقة العميل:</strong> يطلب العميل الاتصال به قبل بدء الصيانة للموافقة على التكلفة المقدرة.
            </Typography>
            <Typography variant="caption" color="warning.dark" display="block" sx={{ mt: 0.5 }}>
              💡 الصيانة متوقفة حتى الحصول على موافقة العميل
            </Typography>
          </Alert>
        )}

        {/* تفاصيل التكلفة */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* التكلفة المبدئية */}
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center" p={1}>
              <Typography variant="caption" color="text.secondary">
                التكلفة المبدئية
              </Typography>
              <Typography variant="h6" color="primary.main">
                {summary.initialCost > 0 ? formatCurrency(summary.initialCost) : '--'}
              </Typography>
              {showActions && !summary.initialCost && onSetInitialCost && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onSetInitialCost}
                  sx={{ mt: 1 }}
                >
                  تحديد
                </Button>
              )}
            </Box>
          </Grid>

          {/* التكلفة المقدرة */}
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center" p={1}>
              <Typography variant="caption" color="text.secondary">
                التكلفة المقدرة
              </Typography>
              <Typography variant="h6" color="info.main">
                {summary.estimatedCost > 0 ? formatCurrency(summary.estimatedCost) : '--'}
              </Typography>
              {showActions && summary.initialCost > 0 && onUpdateEstimatedCost && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onUpdateEstimatedCost}
                  startIcon={<BuildIcon />}
                  sx={{ mt: 1 }}
                >
                  تحديث
                </Button>
              )}
            </Box>
          </Grid>

          {/* التكلفة النهائية */}
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center" p={1}>
              <Typography variant="caption" color="text.secondary">
                التكلفة النهائية
              </Typography>
              <Typography variant="h6" color="success.main">
                {summary.finalCost > 0 ? formatCurrency(summary.finalCost) : '--'}
              </Typography>
              {showActions && currentStage === 'final' && onDeliveryPayment && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={onDeliveryPayment}
                  sx={{ mt: 1 }}
                >
                  تسليم
                </Button>
              )}
            </Box>
          </Grid>

          {/* المبلغ المدفوع */}
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center" p={1}>
              <Typography variant="caption" color="text.secondary">
                المبلغ المدفوع
              </Typography>
              <Typography variant="h6" color="success.main">
                {formatCurrency(summary.totalPaid)}
              </Typography>
              {/* زر الدفعة المقدمة */}
              {showActions && onAdvancePayment &&
               maintenance.status !== 'delivered' &&
               maintenance.status !== 'completed' &&
               (summary.estimatedCost > 0 || summary.initialCost > 0) &&
               summary.totalPaid < (summary.estimatedCost || summary.initialCost) && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={onAdvancePayment}
                  startIcon={<MoneyIcon />}
                  sx={{ mt: 1 }}
                >
                  دفعة مقدمة
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* تفاصيل إضافية */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              تكلفة قطع الغيار
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {formatCurrency(summary.partsCost)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              تكلفة العمالة
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {formatCurrency(summary.laborCost)}
            </Typography>
          </Grid>
        </Grid>

        {/* شريط التقدم للدفع */}
        {summary.finalCost > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" color="text.secondary">
                تقدم الدفع
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {paymentProgress.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={paymentProgress}
              color={paymentProgress === 100 ? 'success' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* المبلغ المتبقي */}
        {summary.remainingAmount > 0 && (
          <Box
            p={2}
            bgcolor={summary.hasDebt ? 'warning.light' : 'error.light'}
            borderRadius={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              {summary.hasDebt ? <ScheduleIcon /> : <WarningIcon />}
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  المبلغ المتبقي: {formatCurrency(summary.remainingAmount)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summary.hasDebt ? 'مسجل كدين' : 'مطلوب دفعه'}
                </Typography>
              </Box>
            </Box>

            {/* أزرار تسديد الدفعات بعد التسليم */}
            {showActions && maintenance.status === 'delivered' && (
              <Box display="flex" gap={1}>
                {onPostDeliveryPayment && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={onPostDeliveryPayment}
                    startIcon={<MoneyIcon />}
                  >
                    تسديد نقدي أو دين
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* حالة السداد الكامل */}
        {summary.remainingAmount === 0 && summary.totalPaid > 0 && (
          <Box
            p={2}
            bgcolor="success.light"
            borderRadius={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <CheckIcon color="success" />
              <Typography variant="body2" color="success.dark" fontWeight="medium">
                تم السداد بالكامل
              </Typography>
            </Box>
          </Box>
        )}

        {/* زر عرض تاريخ المعاملات */}
        {showActions && summary.totalPaid > 0 && onViewPaymentHistory && (
          <Box mt={2} display="flex" justifyContent="center">
            <Button
              size="small"
              variant="outlined"
              onClick={onViewPaymentHistory}
              startIcon={<ScheduleIcon />}
            >
              عرض تاريخ المعاملات
            </Button>
          </Box>
        )}

        {/* زر المرتجعات */}
        {showActions && summary.totalPaid > 0 && onRefund && (maintenance.status === 'refunded' || maintenance.status === 'delivered' || maintenance.status === 'completed') && (
          <Box mt={2} display="flex" justifyContent="center">
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={onRefund}
              startIcon={<WarningIcon />}
            >
              إرجاع مبلغ
            </Button>
          </Box>
        )}

        {/* معلومات إضافية */}
        {maintenance.requireCallBeforeMaintenance && (
          <Box mt={2} p={1} bgcolor="info.light" borderRadius={1}>
            <Typography variant="caption" color="info.dark">
              📞 العميل طلب الاتصال به قبل بدء الصيانة
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CostSummaryCard;
